import type { CSSProperties } from 'react';
// Semantic theme tokens for the block renderers.
//
// Each is a CSS custom property (injected by the tenant layout from the site's
// `style`, and — once wired — by the builder preview) with a BRAND-DEFAULT
// fallback. The fallback means a block still renders correctly where the var
// isn't defined (e.g. an unthemed preview), while a themed surface recolors the
// whole site the moment the palette changes. Blocks must use these instead of
// hardcoded Tailwind colors so the Brand Manager palette actually drives them.
//
// Mapping intent:
//  - secondary  → dark brand surfaces (hero band, footer)
//  - primary    → the action color (CTA band, buttons, links, active states)
//  - surface    → light section fills (search bar, cards)
//  - background → page/header background
//  - text/textMuted → body + secondary text on light surfaces
// Dark surfaces (secondary/primary) pair with white text by design.
import { luminance, onCssVar, readableTextOn, roleByKey, SURFACE_TEXT_COLOR } from '@realsite/site-config';
import { colorToHex } from './colorHex';

/**
 * A section's colour, named by ROLE instead of frozen as a hex — `{ backgroundRole: 'accent' }`
 * rather than `{ backgroundColor: '#0f0f12' }`.
 *
 * This is what look presets use now. The difference is not cosmetic: a hex written into a block
 * row six months ago stays that hex forever, so a look's signature band survived every later
 * brand change. That is the reported bug — "the Noir has black background on sections and when
 * you add colors if none of them are black, it leaves the black as the background". A role
 * reference resolves to the live CSS var, so the band moves when the brand moves.
 *
 * Resolved through the registry's own `cssVar`, so there is no second role→var table here to
 * drift from the one in packages/site-config/colorRoles.ts.
 */
export function roleVar(role: string | undefined, fallback: string): string {
  if (!role) return fallback;
  const def = roleByKey(role);
  // An unknown role falls back rather than emitting `var(undefined)`: a preset naming a role
  // that no longer exists should render the brand default, not a blank section.
  return def ? `var(${def.cssVar}, ${fallback})` : fallback;
}

/**
 * Readable text ON a role's colour — `--color-on-accent` and friends, computed per brand from
 * each role's luminance.
 *
 * This is why a preset no longer writes `textColor: '#ffffff'` beside its band. A pinned white
 * next to a background that follows the brand is a promise that only holds until someone picks a
 * cream accent, at which point the words disappear. Letting the text follow the band is the only
 * version of that promise that survives a brand edit.
 */
export function onRoleVar(role: string | undefined, fallback: string): string {
  if (!role) return fallback;
  const def = roleByKey(role);
  return def ? `var(${onCssVar(def.role)}, ${fallback})` : fallback;
}

/**
 * Resolve a section's background + text color. Precedence, highest first:
 *
 *   1. a per-block HEX (`backgroundColor` / `textColor`) — a tenant who deliberately made one
 *      section a custom colour keeps it, which is why this still wins;
 *   2. a per-block ROLE (`backgroundRole` / `textRole`) — what look presets ship, and what keeps
 *      following the brand;
 *   3. the brand-theme default for that block.
 *
 * Main text elements should INHERIT this `color` (omit their own) so a custom textColor actually
 * applies; muted/secondary text can keep an `opacity-*` treatment so it stays legible on any base
 * color.
 */
export function sectionColors(
  props: { backgroundColor?: string; textColor?: string; backgroundRole?: string; textRole?: string } & SectionLayoutProps & SectionAccentProps,
  defaults: { bg: string; text: string },
): { backgroundColor: string; color: string } & CSSProperties {
  return {
    // Per-section layout (plan E7/E11) rides along as custom properties: every section renderer
    // already spreads this onto its root, so blocks.css can apply them without touching each one.
    ...layoutVars(props),
    // So do the section's icon and link colours (accentVars below).
    ...accentVars(props),
    backgroundColor: props.backgroundColor?.trim()
      ? props.backgroundColor
      : roleVar(props.backgroundRole, defaults.bg),
    // A band that named its background gets text readable ON that background by default. The
    // preset does not have to say anything about text, and cannot get it wrong for a brand it
    // has never seen.
    //
    // But ONLY when the role is what actually painted the background. If an explicit hex won
    // above, deriving the text from the role instead would compute a colour for a background
    // this section does not have — a light custom hex getting the role's white on-colour, which
    // is unreadable. In that case the block's own default is the honest answer.
    color: props.textColor?.trim()
      ? props.textColor
      : props.textRole
        ? roleVar(props.textRole, defaults.text)
        : props.backgroundColor?.trim()
          ? defaults.text
          : onRoleVar(props.backgroundRole, defaults.text),
  };
}

/** Per-section layout choices (plan E7/E11). Blank = the renderer's own spacing / alignment / cards. */
export interface SectionLayoutProps {
  paddingY?: 'none' | 'compact' | 'normal' | 'spacious' | string;
  headingAlign?: 'left' | 'center' | 'right' | string;
  cardStyle?: 'square' | 'rounded' | 'raised' | 'flat' | string;
}

/**
 * A grid section's "Cards per row" choice as fixed Tailwind classes (the strings must stay whole so
 * Tailwind's scanner keeps them). Phones always show one card per row, tablets two, and the chosen
 * number from desktop width up. Blank or any other value → null: the renderer keeps its own grid,
 * so a section that never set it looks exactly as before.
 */
const GRID_COLUMNS: Record<string, string> = {
  '2': 'grid-cols-1 sm:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};
export function gridColumnsClass(columns: unknown): string | null {
  return GRID_COLUMNS[String(columns ?? '')] ?? null;
}

/**
 * Featured listings show two full rows, so how many homes a Listings section holds follows its
 * "Cards per row": 2 → 4, 3 → 6, 4 → 8. Blank (the usual three per row) → 6. Studio's section form
 * and BLT's settings tool both set `limit` from this when the row count changes, so the last row is
 * never half empty. services/api-gateway/src/lib/listings-columns.ts mirrors it (checked by a test).
 */
export const LISTINGS_LIMIT_FOR_COLUMNS: Record<string, number> = { '': 6, '2': 4, '3': 6, '4': 8 };
export function listingsLimitForColumns(columns: unknown): number | null {
  return LISTINGS_LIMIT_FOR_COLUMNS[String(columns ?? '')] ?? null;
}

/** The usual card grid (Listings, Agents, Offices, Communities, Blog list) when no choice is set. */
export const DEFAULT_CARD_GRID = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

/**
 * The one place a grid section picks its column classes: a "List" layout is always one per row,
 * then the "Cards per row" choice, then the section's own default (DEFAULT_CARD_GRID, or a
 * count-based one passed in by Testimonials / Feature grid / Stats).
 */
export function cardGridClass(props: { columns?: unknown; layout?: unknown }, fallback: string = DEFAULT_CARD_GRID): string {
  if (props.layout === 'list') return 'grid-cols-1';
  return gridColumnsClass(props.columns) ?? fallback;
}

const PAD_Y: Record<string, string> = { none: '0px', compact: '2rem', spacious: '6rem' };
const HEADING_ALIGN = new Set(['left', 'center', 'right']);
const CARD_STYLE: Record<string, { radius?: string; shadow?: string }> = {
  square: { radius: '0px' },
  rounded: { radius: '1rem' },
  raised: { radius: '1rem', shadow: '0 12px 32px rgba(0,0,0,.14)' },
  flat: { shadow: 'none' },
};

/**
 * A section's layout choices as custom properties, applied by blocks.css:
 * --blk-pad-y (section top/bottom padding), --blk-heading-align (the section heading, h2), and
 * --blk-card-radius / --blk-card-shadow (its .blk-card / .tmpl-card cards). Only known values map —
 * anything else (and 'normal') sets nothing, so a prop can never inject CSS.
 */
export function layoutVars(p: SectionLayoutProps): CSSProperties {
  const vars: Record<string, string> = {};
  const pad = PAD_Y[String(p.paddingY ?? '')];
  if (pad) vars['--blk-pad-y'] = pad;
  if (HEADING_ALIGN.has(String(p.headingAlign ?? ''))) vars['--blk-heading-align'] = String(p.headingAlign);
  const card = CARD_STYLE[String(p.cardStyle ?? '')];
  if (card?.radius) vars['--blk-card-radius'] = card.radius;
  if (card?.shadow) vars['--blk-card-shadow'] = card.shadow;
  return vars as CSSProperties;
}

/**
 * A tenant/AI-supplied colour, or undefined when it is blank or not a plain CSS colour.
 *
 * Block props like `searchBgColor` end up in inline styles, which the tenant site renders on the
 * server. Only a hex, rgb()/hsl() or a bare colour keyword is accepted — nothing with `;`, `url(`
 * or `var(` — so a prop value can never smuggle in extra declarations. Callers fall back to the
 * theme when this returns undefined.
 */
export function cssColor(value: string | undefined | null): string | undefined {
  const v = (value ?? '').trim();
  if (!v) return undefined;
  if (/^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v)) return v;
  if (/^(?:rgb|rgba|hsl|hsla)\(\s*[0-9.,%\s/-]+\)$/i.test(v)) return v;
  if (/^[a-z]{3,30}$/i.test(v)) return v;
  return undefined;
}

/** A section's own icon and link colours. Blank = each icon / link keeps its usual colour. */
export interface SectionAccentProps {
  iconColor?: string;
  linkColor?: string;
}

/**
 * A section's icon and link colours as --blk-icon / --blk-link. blocks.css paints every icon (svg)
 * and every text link (.blk-link, .tmpl-agent-link, rich-text links) inside the section with them,
 * over the brand primary those draw in by default — the automatic sections (agents, offices, blog,
 * communities, the automatic list) draw both in Primary, which on a pale palette is unreadable.
 * Only a plain colour passes (cssColor), and nothing is set when blank, so a section that never
 * chose one renders exactly as before.
 */
export function accentVars(p: SectionAccentProps): CSSProperties {
  const icon = cssColor(p.iconColor);
  const link = cssColor(p.linkColor);
  return { ...(icon ? { '--blk-icon': icon } : {}), ...(link ? { '--blk-link': link } : {}) } as CSSProperties;
}

/**
 * A section's button colours as --blk-btn-bg/-fg (plan E6/E9). Spread into the SECTION's style:
 * custom properties inherit, so every .blk-btn inside picks them up, hover included (blocks.css).
 */
export function buttonVars(p: { buttonBgColor?: string; buttonTextColor?: string }): CSSProperties {
  const bg = cssColor(p.buttonBgColor);
  const fg = cssColor(p.buttonTextColor);
  return { ...(bg ? { '--blk-btn-bg': bg } : {}), ...(fg ? { '--blk-btn-fg': fg } : {}) } as CSSProperties;
}

/** WCAG contrast of two hexes (null when either is unreadable). */
function contrast(a: string, b: string): number | null {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return null;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Words that read on `bgHex` at 4.5:1 (WCAG body text). `readableTextOn` alone is a luminance
 * cutoff, not a contrast guarantee — on #999999 it picks white at 2.85:1 while the brand ink reaches
 * 6.9:1 — so its pick is tried first and the next candidate that reads wins (as listingInkVars in
 * packages/site-config/pageSurface.ts does); if none reaches 4.5, the strongest.
 */
export function readableInk(bgHex: string): string {
  const candidates = [readableTextOn(bgHex), '#10032A', '#FFFFFF', '#111827', '#000000'];
  const ratio = (c: string) => contrast(c, bgHex) ?? 0;
  return candidates.find((c) => ratio(c) >= 4.5) ?? candidates.reduce((a, b) => (ratio(b) > ratio(a) ? b : a));
}

/** `share` of hex `a` over hex `b` (what `color-mix(in srgb, a share, b)` paints). */
function mixHex(a: string, b: string, share: number): string {
  const ch = (h: string, i: number) => parseInt(h.slice(1 + i, 3 + i), 16);
  return `#${[0, 2, 4].map((i) => Math.round(ch(a, i) * share + ch(b, i) * (1 - share)).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Captions (bylines, office labels: small text) beside `ink` on `bgHex`: the ink softened 72% toward
 * the background when that still reads at 4.5:1, else the ink itself. Both must be hexes.
 */
export function quietInk(ink: string, bgHex: string): string {
  const inkHex = colorToHex(ink);
  if (!inkHex) return ink;
  const soft = mixHex(inkHex, bgHex, 0.72);
  return (contrast(soft, bgHex) ?? 0) >= 4.5 ? soft : ink;
}

/** Words on the page background, readable whatever the site's Text colour (site-config listingInkVars). */
export const PAGE_TEXT_COLOR = 'var(--color-ink-page, var(--color-text))';

/**
 * The words on a section's cards. A card is its own panel: it does NOT share the section's band
 * colour, so the section's text colour is not automatically readable on it.
 *
 * Reported 2026-10-22 (automatic sections audit): Agents / Offices / Communities / Blog list /
 * Testimonials / Agent profile / Contact details cards kept a light card while inheriting the
 * section's text, so "dark band + white text" put white words on a light card, and a dark Surface
 * with dark Text put dark words on a dark card.
 *
 *   - `cardBg` a colour (the owner's Card colour, or Testimonials' white): the section's own Text
 *     colour when it reads there (4.5:1), else readable ink for that colour. A default white card in
 *     a section that changed nothing keeps inheriting, so it looks exactly as before.
 *   - `cardBg` a theme var (`surfaceInk` names the readable ink for it): that ink, unless the owner
 *     set only a Text colour on an unpainted section — that choice was made for the page the cards
 *     sit on, so it is kept.
 *
 * Returns undefined to inherit the section's colour.
 */
export function cardTextColor(
  section: { backgroundColor?: string; textColor?: string; backgroundRole?: string; textRole?: string },
  card: { cardBg?: string | null; surfaceInk?: string; customCard?: boolean },
): string | undefined {
  const band = Boolean(section.backgroundColor?.trim() || section.backgroundRole?.trim() || section.textRole?.trim());
  const text = colorToHex(section.textColor);
  const hex = colorToHex(card.cardBg ?? undefined);
  if (hex) {
    if (text && (contrast(text, hex) ?? 0) >= 4.5) return undefined;
    if (!text && !band && !card.customCard) return undefined;
    return readableInk(hex);
  }
  if (text && !band) return undefined;
  return card.surfaceInk ?? SURFACE_TEXT_COLOR;
}

/** The settings an Automatic list keeps (AutomaticContentBlockProps). */
export interface AutomaticListLookProps extends SectionAccentProps {
  backgroundColor?: string;
  textColor?: string;
  columns?: string;
  cardBgColor?: string;
  cardStyle?: string;
}

/**
 * An Automatic list's settings as custom properties + a data attribute, for the wrapper around the
 * list the host draws (AutomaticContent.tsx). The lists (AgentRoster, BlogPostCardList,
 * CommunityCardList, SoldCardList, ComingSoonTeaser) are written with Tailwind whites and greys;
 * blocks.css repaints them through the hooks they carry (.auto-list-band / -card / -grid / -pad):
 *
 *   --auto-list-bg / --auto-list-text (+ -muted): the list's bands and the words on them. A
 *     background with no text colour gets readable ink for it (readableInk); a chosen text colour
 *     is kept as chosen. The muted captions are checked too (quietInk), else they are the text.
 *   --auto-list-card / --auto-list-card-ink (+ -muted): the cards, with readable words.
 *   --blk-card-radius / --blk-card-shadow: Card style, as on every other section.
 *   data-cols: Cards per row.
 *
 * Only plain colours pass (cssColor). Nothing chosen = nothing set.
 */
export function automaticListLook(p: AutomaticListLookProps): { style: CSSProperties; cols?: string } {
  const vars: Record<string, string> = { ...(accentVars(p) as Record<string, string>) };
  const card = CARD_STYLE[String(p.cardStyle ?? '')];
  if (card?.radius) vars['--blk-card-radius'] = card.radius;
  if (card?.shadow) vars['--blk-card-shadow'] = card.shadow;
  const bg = cssColor(p.backgroundColor);
  const bgHex = colorToHex(bg);
  const text = cssColor(p.textColor) ?? (bgHex ? readableInk(bgHex) : undefined);
  if (bg) vars['--auto-list-bg'] = bg;
  if (text) {
    vars['--auto-list-text'] = text;
    // Over a background we can't measure (none chosen: the page's), captions are the text itself.
    vars['--auto-list-muted'] = bgHex ? quietInk(text, bgHex) : text;
  }
  const cardBg = cssColor(p.cardBgColor);
  const cardHex = colorToHex(cardBg);
  if (cardBg && cardHex) {
    const ink = readableInk(cardHex);
    vars['--auto-list-card'] = cardBg;
    vars['--auto-list-card-ink'] = ink;
    vars['--auto-list-card-muted'] = quietInk(ink, cardHex);
  }
  const cols = ['2', '3', '4'].includes(String(p.columns ?? '')) ? String(p.columns) : undefined;
  return { style: vars as CSSProperties, cols };
}

export const theme = {
  primary: 'var(--color-primary, #4E1F60)',
  secondary: 'var(--color-secondary, #10032A)',
  accent: 'var(--color-accent, #C9B8F4)',
  background: 'var(--color-background, #ffffff)',
  surface: 'var(--color-surface, #f8f6ff)',
  text: 'var(--color-text, #10032A)',
  textMuted: 'var(--color-text-muted, #6E6680)',
  // Readable text ON the primary/secondary brand surfaces — the tenant layout injects these from
  // each color's WCAG luminance, so white text never lands on a LIGHT brand (the invisible
  // footer-text bug on light-tan secondary). Fall back to white for the common dark-brand case.
  onPrimary: 'var(--color-on-primary, #ffffff)',
  onSecondary: 'var(--color-on-secondary, #ffffff)',
  // Text where the background is dark or is a PHOTO — the tenant's `text_on_dark`.
  //
  // Distinct from onSecondary, which asks "what reads on the secondary colour". Over a hero
  // image the secondary is not what is behind the words: the photo is, under a scrim. A brand
  // with a light secondary therefore got dark ink laid over a dark photograph, computed
  // correctly for a background that section does not have.
  onDark: 'var(--color-text-on-dark, #ffffff)',
  // Roles that were editable in the Brand Manager but painted nothing until the color registry
  // wired them up (docs/COLOR_SYSTEM_PLAN.md Phase 1). Each falls back to what it DERIVES from,
  // so an unthemed surface renders exactly as it did before these existed.
  neutralDark: 'var(--color-neutral-dark, #10032A)',
  neutralLight: 'var(--color-neutral-light, #f8f6ff)',
  footerBg: 'var(--color-footer-bg, var(--color-secondary, #10032A))',
  // Readable text ON the footer's OWN colour. The footer used to borrow `onSecondary`, which was
  // right only while the footer was always the secondary — now that it is an independently
  // editable role, a light authored footer would get white-on-light from a var computed for a
  // colour it no longer uses.
  onFooter: 'var(--color-on-footer, var(--color-on-secondary, #ffffff))',
  buttonBg: 'var(--color-button-primary-bg, var(--color-primary, #4E1F60))',
  buttonText: 'var(--color-button-primary-text, var(--color-on-primary, #ffffff))',
  buttonHover: 'var(--color-button-primary-hover, var(--color-button-primary-bg, var(--color-primary, #4E1F60)))',
  link: 'var(--color-link, var(--color-primary, #4E1F60))',
  linkHover: 'var(--color-link-hover, var(--color-link, var(--color-primary, #4E1F60)))',
  headingFont: 'var(--font-heading, inherit)',
  bodyFont: 'var(--font-body, inherit)',
} as const;
